import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { confirmPaydunyaPayment } from '@/lib/ads-campaigns';

function PaymentCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'loading' | 'success' | 'failed' | 'cancelled' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Aucun token de paiement trouvé dans l\'URL.');
      return;
    }

    const confirm = async () => {
      try {
        const result = await confirmPaydunyaPayment(token);

        if (result.success) {
          setStatus('success');
          setMessage(
            'Votre paiement a été effectué avec succès. Votre campagne publicitaire sera activée sous peu.',
          );
        } else if (result.status === 'cancelled') {
          setStatus('cancelled');
          setMessage('Le paiement a été annulé.');
        } else {
          setStatus('failed');
          setMessage(`Le paiement n'a pas pu être confirmé. Statut : ${result.status}.`);
        }
      } catch (err) {
        setStatus('error');
        setMessage(
          err instanceof Error
            ? err.message
            : 'Erreur lors de la confirmation du paiement.',
        );
      }
    };

    confirm();
  }, [token]);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center py-20 px-4">
        <div className="max-w-md w-full mx-auto text-center">
          {status === 'loading' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-loader-4-line text-4xl text-primary-400 animate-spin" />
              </div>
              <h2 className="font-heading font-bold text-xl text-foreground-50 mb-2">
                Confirmation du paiement…
              </h2>
              <p className="text-sm text-foreground-400">
                Veuillez patienter pendant que nous vérifions votre transaction.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-500/15">
                <i className="ri-checkbox-circle-line text-4xl text-accent-400" />
              </div>
              <h2 className="font-heading font-bold text-xl text-foreground-50 mb-2">
                Paiement confirmé !
              </h2>
              <p className="text-sm text-foreground-400 mb-6">{message}</p>
              <button
                type="button"
                onClick={() => navigate('/ads')}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" />
                Retour à WorldApeiron Ads
              </button>
            </>
          )}

          {(status === 'failed' || status === 'cancelled' || status === 'error') && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100/10">
                <i className="ri-error-warning-line text-4xl text-accent-400" />
              </div>
              <h2 className="font-heading font-bold text-xl text-foreground-50 mb-2">
                {status === 'cancelled' ? 'Paiement annulé' : 'Erreur de paiement'}
              </h2>
              <p className="text-sm text-accent-400 mb-6">{message}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  type="button"
                  onClick={() => navigate('/ads')}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line" />
                  Retour aux offres
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/support')}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg border border-foreground-300/40 text-foreground-50 font-semibold text-sm hover:bg-foreground-50/10 transition-colors whitespace-nowrap"
                >
                  <i className="ri-customer-service-2-line" />
                  Contacter le support
                </button>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default PaymentCallbackPage;